import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';

export interface MultiselectOption {
  value: string;
  label: string;
  count?: number;
  /** Optional colored dot shown before the label (e.g. campaign color). */
  dot?: string;
}

export interface MultiSelectPopoverProps {
  /** Button label shown when nothing is selected. */
  placeholder: string;
  options: MultiselectOption[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  /** iconify icon for the trigger button. */
  icon?: string;
  /** Keep the trigger compact (icon only when collapsed). */
  compact?: boolean;
}

const MENU_MIN_WIDTH = 220;
const MENU_MAX_WIDTH = 300;
const MENU_MAX_HEIGHT = 340;
const VIEWPORT_PADDING = 12;

/**
 * MultiSelectPopover — a checkbox list in a dropdown, used for the Calendar/
 * Kanban filter bar (campaigns, statuses, channels). Click-outside + Esc to
 * close. Selecting is additive; a clear control resets to empty (= all).
 */
export const MultiSelectPopover: React.FC<MultiSelectPopoverProps> = ({ placeholder, options, selected, onChange, icon, compact }) => {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    left: number;
    top: number;
    width: number;
    maxHeight: number;
    placement: 'top' | 'bottom';
  } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const selectedCount = selected.size;

  const updateMenuPosition = useCallback(() => {
    if (typeof window === 'undefined') return;

    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const width = Math.min(MENU_MAX_WIDTH, Math.max(MENU_MIN_WIDTH, rect.width));
    const leftLimit = Math.max(VIEWPORT_PADDING, window.innerWidth - width - VIEWPORT_PADDING);
    const left = Math.min(Math.max(rect.left, VIEWPORT_PADDING), leftLimit);
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
    const spaceAbove = rect.top - VIEWPORT_PADDING;
    const placement = spaceBelow < 240 && spaceAbove > spaceBelow ? 'top' : 'bottom';
    const availableSpace = placement === 'top' ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(180, Math.min(MENU_MAX_HEIGHT, availableSpace - 8));
    const measuredHeight = menuRef.current?.offsetHeight ?? Math.min(MENU_MAX_HEIGHT, 48 + Math.min(options.length || 1, 7) * 40);
    const menuHeight = Math.min(measuredHeight, maxHeight);
    const top =
      placement === 'top'
        ? Math.max(VIEWPORT_PADDING, rect.top - menuHeight - 8)
        : Math.min(rect.bottom + 8, window.innerHeight - VIEWPORT_PADDING - menuHeight);

    setMenuPosition({ left, top, width, maxHeight, placement });
  }, [options.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      const isInsideTrigger = Boolean(containerRef.current?.contains(target));
      const isInsideMenu = Boolean(menuRef.current?.contains(target));

      if (!isInsideTrigger && !isInsideMenu) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    updateMenuPosition();
    const frame = window.requestAnimationFrame(updateMenuPosition);
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(next);
  };

  const buttonLabel = selectedCount === 0 ? placeholder : selectedCount === 1 ? options.find((o) => selected.has(o.value))?.label || placeholder : `${placeholder} · ${selectedCount}`;

  const menu =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-multiselectable="true"
            style={
              menuPosition
                ? {
                    left: menuPosition.left,
                    top: menuPosition.top,
                    width: menuPosition.width,
                    maxHeight: menuPosition.maxHeight,
                  }
                : undefined
            }
            className={`fixed z-[70] rounded-[16px] border border-white/12 bg-[#101217]/98 p-1.5 shadow-[0_24px_60px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.035)] backdrop-blur-xl ${
              menuPosition?.placement === 'top' ? 'origin-bottom' : 'origin-top'
            }`}
          >
            <div className="flex items-center justify-between px-2 py-1">
              <span className="text-[10px] font-black uppercase tracking-[0.16em] text-white/35">{placeholder}</span>
              {selectedCount > 0 && (
                <button type="button" onClick={() => onChange(new Set())} className="inline-flex items-center gap-1 text-[10px] font-bold text-white/40 hover:text-white/70">
                  <Icon icon="bx:reset" className="h-3 w-3" />
                  Clear
                </button>
              )}
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: menuPosition ? Math.max(120, menuPosition.maxHeight - 38) : 280 }}>
              {options.map((option) => {
                const active = selected.has(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => toggle(option.value)}
                    className={`flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left text-xs transition-colors ${active ? 'bg-cyan-400/10 text-cyan-50' : 'text-white/70 hover:bg-white/[0.05]'}`}
                  >
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${active ? 'border-cyan-300/50 bg-cyan-400/20' : 'border-white/15'}`}>
                      {active && <Icon icon="bx:check" className="h-3 w-3 text-cyan-200" />}
                    </span>
                    {option.dot && <span className={`h-2 w-2 shrink-0 rounded-full ${option.dot}`} />}
                    <span className="min-w-0 flex-1 truncate font-semibold">{option.label}</span>
                    {option.count !== undefined && <span className="text-[10px] text-white/35">{option.count}</span>}
                  </button>
                );
              })}
              {options.length === 0 && <p className="px-2 py-3 text-center text-[11px] text-white/35">Nothing to filter</p>}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-2.5 text-[10px] font-black transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan-300/[0.24] ${
          selectedCount > 0
            ? 'border-transparent bg-cyan-400/[0.075] text-cyan-100'
            : 'border-transparent bg-white/[0.018] text-white/55 hover:bg-white/[0.045] hover:text-white/80'
        }`}
      >
        {icon && <Icon icon={icon} className="h-3.5 w-3.5" />}
        {!compact && <span className="max-w-[150px] truncate">{buttonLabel}</span>}
        <Icon icon={open ? 'bx:chevron-up' : 'bx:chevron-down'} className="h-3.5 w-3.5 opacity-60" />
      </button>

      {menu}
    </div>
  );
};

export default MultiSelectPopover;
